import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { REQUIRED, stepName } from '@platform/schema/onboarding-flow';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { DragList } from '../ordering/DragList';
import { moved } from '../ordering/moved';

import type {
  FlowStep,
  OnboardingChallenge,
  OnboardingFlow,
} from '@platform/schema/onboarding';
import type { FlowProblem } from '@platform/schema/onboarding-flow';

const KIND: Record<FlowStep['kind'], string> = {
  BUILT_IN: 'App screen',
  INFO: 'Information',
  QUESTION: 'Question',
};

/** Steps the app skips for some learners on its own, whatever the flow says. */
const SOMETIMES = new Set(['STARTING_FOCUS', 'FAMILIARITY']);

/**
 * The flow in order — ONB-19. Each row is one screen: where it sits, what it is, whether
 * every learner sees it, and whether something on it needs fixing.
 */
export function StepList({
  flow,
  selected,
  problems,
  challenge,
  disabled,
  onSelect,
  onChange,
}: {
  flow: OnboardingFlow;
  selected: string;
  problems: readonly FlowProblem[];
  challenge: OnboardingChallenge | null;
  disabled: boolean;
  onSelect: (id: string) => void;
  onChange: (flow: OnboardingFlow) => void;
}) {
  return (
    <Stack spacing={1} component="nav" aria-label="Onboarding steps">
      <DragList
        disabled={disabled}
        onMove={(from, to) => {
          onChange(moved(flow, from, to));
        }}
      >
        {flow.map((step, at) => {
          const position = flow
            .slice(0, at + 1)
            .filter((one) => one.enabled).length;
          return (
            <Row
              key={step.id}
              step={step}
              position={step.enabled ? position : undefined}
              selected={step.id === selected}
              problems={problems.filter((one) => one.step === step.id)}
              challenge={step.id === 'FIRST_CHALLENGE' ? challenge : undefined}
              disabled={disabled}
              onSelect={() => {
                onSelect(step.id);
              }}
              onMove={(by) => {
                onChange(moved(flow, at, at + by));
              }}
              first={at === 0}
              last={at === flow.length - 1}
            />
          );
        })}
      </DragList>
    </Stack>
  );
}

function Row({
  step,
  position,
  selected,
  problems,
  challenge,
  disabled,
  onSelect,
  onMove,
  first,
  last,
}: {
  step: FlowStep;
  position: number | undefined;
  selected: boolean;
  problems: readonly FlowProblem[];
  challenge: OnboardingChallenge | null | undefined;
  disabled: boolean;
  onSelect: () => void;
  onMove: (by: 1 | -1) => void;
  first: boolean;
  last: boolean;
}) {
  const [open, setOpen] = useState(false);
  const blocked = problems.some((one) => one.level === 'block');
  const name = stepName(step);
  const optional = step.kind === 'BUILT_IN' && REQUIRED[step.id] === undefined;

  return (
    <Box
      data-testid={`step-${step.id}`}
      sx={{
        border: 1,
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: 'background.paper',
        opacity: step.enabled ? 1 : 0.6,
      }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', p: 1 }}>
        <ButtonBase
          onClick={onSelect}
          aria-current={selected ? 'step' : undefined}
          sx={{
            flex: 1,
            justifyContent: 'flex-start',
            textAlign: 'left',
            columnGap: 1,
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ minWidth: '2ch', textAlign: 'right' }}
          >
            {position ?? '–'}
          </Typography>
          <Stack sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {name}
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
              <Typography variant="caption" color="text.secondary">
                {KIND[step.kind]}
              </Typography>
              {!step.enabled && <Chip size="small" label="Off" />}
              {step.enabled && (optional || step.kind !== 'BUILT_IN') && (
                <Chip size="small" variant="outlined" label="Optional" />
              )}
              {(step.showIf !== undefined || SOMETIMES.has(step.id)) && (
                <Chip size="small" variant="outlined" label="Conditional" />
              )}
            </Stack>
          </Stack>
          {problems.length > 0 && (
            <Chip
              size="small"
              color={blocked ? 'error' : 'warning'}
              label={blocked ? 'Fix' : 'Check'}
              aria-label={`${String(problems.length)} problem(s) on ${name}`}
            />
          )}
        </ButtonBase>
        <Stack>
          <Button
            size="small"
            sx={{ minWidth: 0, py: 0 }}
            aria-label={`Move ${name} up`}
            disabled={disabled || first}
            onClick={() => {
              onMove(-1);
            }}
          >
            ▲
          </Button>
          <Button
            size="small"
            sx={{ minWidth: 0, py: 0 }}
            aria-label={`Move ${name} down`}
            disabled={disabled || last}
            onClick={() => {
              onMove(1);
            }}
          >
            ▼
          </Button>
        </Stack>
      </Stack>
      {challenge !== undefined && (
        <Box sx={{ px: 2, pb: 1 }}>
          <Button
            size="small"
            aria-expanded={open}
            onClick={() => {
              setOpen(!open);
            }}
          >
            {challenge === null
              ? 'Not published'
              : `${String(challenge.questions.length)} questions`}
          </Button>
          {open && challenge !== null && (
            <Stack component="ol" spacing={0.5} sx={{ pl: 3, m: 0 }}>
              {challenge.questions.map((question) => (
                <Typography
                  component="li"
                  variant="caption"
                  key={question.sceneId}
                >
                  <Link
                    component={RouterLink}
                    to={`/authoring/lessons/${challenge.lessonId}`}
                  >
                    {question.prompt}
                  </Link>
                </Typography>
              ))}
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );
}
